import { useSEO, useStructuredData } from '../hooks/useSEO';

export const SEO = ({ 
  title, 
  description, 
  keywords = "",
  canonicalUrl = "",
  ogImage = "",
  schemaData = null
}) => {
  // Base URL for canonical and OG
  const baseUrl = process.env.REACT_APP_BACKEND_URL 
    ? process.env.REACT_APP_BACKEND_URL.replace('/api', '')
    : 'https://box7go.com.br';

  // Use the SEO hook
  useSEO({
    title,
    description,
    keywords,
    canonicalUrl: canonicalUrl || `${baseUrl}${window.location.pathname}`,
    ogImage: ogImage || 'https://customer-assets.emergentagent.com/job_6f0edc25-344f-42c2-bad9-83f2f1db633b/artifacts/9w2b2owp_Box%207%20estetica%20e%20lava%20jato%20-%20Logo.png',
    ogType: 'website'
  });

  // Use structured data hook
  useStructuredData(schemaData);

  // This component doesn't render anything
  return null;
};

// Helper function for generating local business schema
export const generateLocalBusinessSchema = (customData = {}) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL 
    ? process.env.REACT_APP_BACKEND_URL.replace('/api', '')
    : 'https://box7go.com.br';

  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "AutoWash", "AutomotiveService"],
    "name": "Box7go Estética e Lava Jato",
    "image": [
      "https://customer-assets.emergentagent.com/job_6f0edc25-344f-42c2-bad9-83f2f1db633b/artifacts/9w2b2owp_Box%207%20estetica%20e%20lava%20jato%20-%20Logo.png"
    ],
    "description": "Estética automotiva e lava jato premium em Goiânia e Aparecida de Goiânia. Lavagem automotiva, detalhamento automotivo e serviços especializados com produtos profissionais.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Jardim Bela Vista",
      "addressLocality": "Goiânia",
      "addressRegion": "GO",
      "postalCode": "74000-000",
      "addressCountry": "BR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "-16.6869",
      "longitude": "-49.2648"
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Goiânia",
        "addressRegion": "GO",
        "addressCountry": "BR"
      },
      {
        "@type": "City",
        "name": "Aparecida de Goiânia",
        "addressRegion": "GO",
        "addressCountry": "BR"
      }
    ],
    "telephone": "+5562982416008",
    "url": baseUrl,
    "priceRange": "$$",
    "currenciesAccepted": "BRL",
    "paymentAccepted": "Dinheiro, Cartão de Crédito, Cartão de Débito, PIX",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "08:00",
        "closes": "17:00"
      }
    ],
    "sameAs": [
      "https://www.instagram.com/box7.go/",
      "https://maps.app.goo.gl/u4Shanyp2XZS3BHZ8"
    ],
    "hasMap": "https://maps.app.goo.gl/u4Shanyp2XZS3BHZ8",
    ...customData
  };
};

// Helper function for service pages with specific service schema
export const generateServiceSchema = (serviceName, serviceDescription, serviceDuration, customData = {}) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL 
    ? process.env.REACT_APP_BACKEND_URL.replace('/api', '')
    : 'https://box7go.com.br';

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": serviceName,
    "name": serviceName,
    "description": serviceDescription,
    "provider": {
      "@type": ["LocalBusiness", "AutoWash"],
      "name": "Box7go Estética e Lava Jato",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Jardim Bela Vista",
        "addressLocality": "Goiânia",
        "addressRegion": "GO",
        "postalCode": "74000-000",
        "addressCountry": "BR"
      },
      "telephone": "+5562982416008",
      "priceRange": "$$",
      "url": baseUrl
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Goiânia",
        "addressRegion": "GO",
        "addressCountry": "BR"
      },
      {
        "@type": "City",
        "name": "Aparecida de Goiânia",
        "addressRegion": "GO",
        "addressCountry": "BR"
      }
    ],
    "hoursAvailable": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "08:00",
      "closes": "18:00"
    },
    ...customData
  };
};

// Helper function for local service pages (neighborhood-specific)
export const generateLocalServiceSchema = (neighborhood, city, serviceType, customData = {}) => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL 
    ? process.env.REACT_APP_BACKEND_URL.replace('/api', '')
    : 'https://box7go.com.br';

  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "AutoWash", "AutomotiveService"],
    "name": "Box7go Estética e Lava Jato",
    "description": `${serviceType} em ${neighborhood}, ${city}. Serviços especializados de lavagem automotiva e estética automotiva com produtos profissionais.`,
    "image": [
      "https://customer-assets.emergentagent.com/job_6f0edc25-344f-42c2-bad9-83f2f1db633b/artifacts/9w2b2owp_Box%207%20estetica%20e%20lava%20jato%20-%20Logo.png"
    ],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Jardim Bela Vista",
      "addressLocality": city,
      "addressRegion": "GO",
      "postalCode": "74000-000",
      "addressCountry": "BR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "-16.6869",
      "longitude": "-49.2648"
    },
    "areaServed": {
      "@type": "Place",
      "name": neighborhood,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": city,
        "addressRegion": "GO",
        "addressCountry": "BR"
      }
    },
    "telephone": "+5562982416008",
    "url": baseUrl,
    "priceRange": "$$",
    "currenciesAccepted": "BRL",
    "paymentAccepted": "Dinheiro, Cartão de Crédito, Cartão de Débito, PIX",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "08:00",
        "closes": "17:00"
      }
    ],
    "sameAs": [
      "https://www.instagram.com/box7.go/",
      "https://maps.app.goo.gl/u4Shanyp2XZS3BHZ8"
    ],
    "hasMap": "https://maps.app.goo.gl/u4Shanyp2XZS3BHZ8",
    ...customData
  };
};
